import { useMemo, useCallback, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import moment from 'moment';
import { Box, Typography, styled, InputAdornment } from '@mui/material';
import Grid from '../components/Grid';
import DateRangePicker from '../components/DateRangePicker';
import StatusBadge from '../components/StatusBadge';
import InputField from '../components/InputField';
import {
  FilterArrow,
  FilterButton,
  FilterButtonCaption,
  FilterButtonLabel,
  FilterMenu,
  FilterToolbar,
  GridHeader,
  SearchInputWrapper,
  SortMenuItem,
  SortMenuSectionHeader,
  StatusMenu,
  StatusMenuItem,
  ThemedSearchIcon,
} from '../components/FilterToolbarStyles';
import { formatDate } from '../helpers/date';
import api from '../services/api';
import { useWithdrawals } from '../hooks/withdrawals';
import { DriversCheckboxCell, DriversCheckboxHeader } from '../components/drivers';
import TransactionsFooterRow from '../components/transactions/TransactionsFooterRow';
import { ReactComponent as ArrowLeftIcon } from '../assets/icons/24/arrow_left.svg';
import { STATUS_VARIANTS } from '../constants/common';
import { getTransactionStatusLabel } from '../helpers/transaction';
import { THEME_MODES } from '../theme';
import { PATH } from '../constants/routes';
import SvgIcon from '../components/SvgIcon';

const UserInfoCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: 24,
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.mode === 'dark' ? '#232323' : theme.palette.background.paper,
  marginBottom: 20,
  marginRight: 32,
}));

const UserInfoLeft = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

const AvailableBalanceBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 16,
  padding: 24,
  borderRadius: 14,
  backgroundColor: theme.palette.mode === 'dark' ? '#303030' : 'rgba(0,0,0,0.04)',
  '& .iban-box': {
    border: `1px solid ${theme.palette.divider}`,
  },
}));

const SORT_OPTIONS = {
  date: {
    label: 'Trier par',
    options: [
      { value: 'newest', label: 'Récent → Ancien' },
      { value: 'oldest', label: 'Ancien → Récent' },
    ],
  },
};

const Body = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  height: '100%',
  margin: '0px 16px 16px 0px',
  overflow: 'hidden',
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === THEME_MODES.DARK ? '#232323' : theme.palette.background.default,
}));

const getRowId = (params) => params.data?.id;
const isFullWidthRow = ({ rowNode }) => rowNode?.data?.id === 'footer';

const mapWithdrawalToRow = (w) => {
  const [credential] = w.user?.credentials ?? [];
  const partialCardNumber = credential?.iban?.slice(-4);

  return {
    id: w.id,
    email: w.user?.email,
    name: w.user?.name ?? '',
    date: w.createdAt,
    status: w.status,
    amount: w.amount || 0,
    cardNumber: partialCardNumber,
  };
};

const UserTransactionsScreen = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { data: withdrawals, loading, refetch } = useWithdrawals();
  const [userDetails, setUserDetails] = useState(null);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [statusTransaction, setStatusTransaction] = useState(null);
  const sortOpen = Boolean(sortAnchorEl);
  const statusMenuOpen = Boolean(statusAnchorEl);
  const [sortConfig, setSortConfig] = useState({ value: 'newest' });
  const [dateFilterRange, setDateFilterRange] = useState({ start: null, end: null });

  const userWithdrawals = useMemo(
    () => (withdrawals || []).filter((w) => String(w.user?.id) === String(userId)),
    [withdrawals, userId]
  );

  const transactions = useMemo(() => userWithdrawals.map(mapWithdrawalToRow), [userWithdrawals]);

  const userInfo = useMemo(() => {
    const first = userWithdrawals[0];
    const source = first?.user ?? userDetails;
    if (!source) return null;
    const [credential] = source.credentials || [];
    return {
      id: source.id,
      name: (source.name ?? '').toUpperCase(),
      email: source.email,
      ibanLast4: credential?.iban?.slice(-4),
    };
  }, [userWithdrawals, userDetails]);

  useEffect(() => {
    if (!userId) return;
    const fetchUser = async () => {
      try {
        const response = await api.get(`/users/${userId}`, { skipErrorToast: true });
        if (response?.ok && response.data?.data) {
          setUserDetails(response.data.data);
        }
      } catch {
        setUserDetails(null);
      }
    };
    fetchUser();
  }, [userId]);

  const filteredData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const { start, end } = dateFilterRange;

    return transactions.filter((tx) => {
      const matchesSearch =
        !query ||
        (tx.name || '').toLowerCase().includes(query) ||
        (tx.email || '').toLowerCase().includes(query) ||
        (tx.transactionId || '').includes(query);

      const txMoment = moment(tx.date);
      const matchesDate =
        (!start && !end) ||
        (start &&
          end &&
          txMoment.isSameOrAfter(start, 'day') &&
          txMoment.isSameOrBefore(end, 'day')) ||
        (start && !end && txMoment.isSameOrAfter(start, 'day')) ||
        (!start && end && txMoment.isSameOrBefore(end, 'day'));

      return matchesSearch && matchesDate;
    });
  }, [transactions, searchQuery, dateFilterRange]);

  const sortedData = useMemo(() => {
    const arr = [...filteredData];
    arr.sort((a, b) => {
      const dateA = new Date(a.date || 0).getTime();
      const dateB = new Date(b.date || 0).getTime();
      return sortConfig.value === 'oldest' ? dateA - dateB : dateB - dateA;
    });
    return arr;
  }, [filteredData, sortConfig]);

  const rows = useMemo(
    () =>
      sortedData.map((tx) => ({
        ...tx,
        isChecked: checkedIds.has(tx.id),
      })),
    [sortedData, checkedIds]
  );

  const handleBack = useCallback(() => {
    navigate(PATH.TRANSACTIONS);
  }, [navigate]);

  const handleToggle = useCallback((id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    setCheckedIds((prev) => (prev.size ? new Set() : new Set(rows.map((r) => r.id))));
  }, [rows]);

  const handleSortOpen = (e) => setSortAnchorEl(e.currentTarget);
  const handleSortClose = () => setSortAnchorEl(null);

  const handleSortSelect = useCallback((value) => {
    setSortConfig({ value });
    setSortAnchorEl(null);
  }, []);

  const handleRejectAll = useCallback(async () => {
    const ids = Array.from(checkedIds);
    if (!ids.length) return;

    const response = await api.patch('/withdrawals/bulk-status', {
      ids,
      status: STATUS_VARIANTS.rejected,
    });
    if (!response?.ok) return;

    refetch();
    setCheckedIds(new Set());
  }, [checkedIds, refetch]);

  const handleApproveAll = useCallback(async () => {
    const ids = Array.from(checkedIds);
    if (!ids.length) return;

    const response = await api.patch('/withdrawals/bulk-status', {
      ids,
      status: STATUS_VARIANTS.completed,
    });
    if (!response?.ok) return;

    refetch();
    setCheckedIds(new Set());
  }, [checkedIds, refetch]);

  const isAllChecked = checkedIds.size > 0 && rows.length === checkedIds.size;

  const handleStatusClick = useCallback((event, data) => {
    setStatusAnchorEl(event.currentTarget);
    setStatusTransaction(data);
  }, []);

  const handleStatusMenuClose = useCallback(() => {
    setStatusAnchorEl(null);
    setStatusTransaction(null);
  }, []);

  const handleStatusSelect = useCallback(
    async (newStatus) => {
      if (!statusTransaction) return;

      const response = await api.patch('/withdrawals/bulk-status', {
        ids: [statusTransaction.id],
        status: newStatus,
      });
      if (!response?.ok) return;

      refetch();
      handleStatusMenuClose();
    },
    [statusTransaction, refetch, handleStatusMenuClose]
  );

  const fullWidthCellRenderer = useCallback(
    (params) => (
      <TransactionsFooterRow
        data={params.data}
        onRejectAll={handleRejectAll}
        onApproveAll={handleApproveAll}
      />
    ),
    [handleRejectAll, handleApproveAll]
  );

  const pinnedBottomRowData = useMemo(
    () => (checkedIds.size ? [{ id: 'footer', size: checkedIds.size }] : undefined),
    [checkedIds.size]
  );

  const columnDefs = useMemo(
    () => [
      {
        headerComponent: DriversCheckboxHeader,
        cellRenderer: DriversCheckboxCell,
        width: 63,
        maxWidth: 63,
        minWidth: 63,
        pinned: 'left',
      },
      {
        field: 'cardNumber',
        headerName: 'Numéro de carte',
        initialWidth: 260,
        minWidth: 200,
        valueFormatter: ({ data: { cardNumber } }) => (cardNumber ? `IBAN **** ${cardNumber}` : ''),
      },
      {
        field: 'date',
        headerName: 'Date',
        initialWidth: 140,
        minWidth: 120,
        valueFormatter: ({ data }) => formatDate(data.date, { format: 'medium' }),
      },
      {
        field: 'status',
        headerName: 'Statut',
        minWidth: 110,
        cellRenderer: ({ data }) => (
          <StatusBadge
            active
            variant={data.status}
            sx={{ cursor: 'pointer' }}
            onClick={(e) => handleStatusClick(e, data)}
          >
            {getTransactionStatusLabel(data.status)}
          </StatusBadge>
        ),
      },
      {
        flex: 1,
        field: 'amount',
        headerName: 'Montant (€)',
        minWidth: 110,
        valueFormatter: ({ data }) => data.amount ?? '',
        headerAlign: 'right',
        cellClass: 'ag-cell-right',
      },
    ],
    [handleStatusClick]
  );

  const defaultColDef = useMemo(
    () => ({
      initialWidth: 140,
      minWidth: 100,
      cellRendererParams: {
        onToggle: handleToggle,
      },
      headerComponentParams: {
        isAllChecked,
        onToggleAll: handleToggleAll,
      },
    }),
    [handleToggle, isAllChecked, handleToggleAll]
  );

  if (!userId) {
    return null;
  }

  return (
    <>
      <UserInfoCard>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 3,
            cursor: 'pointer',
            color: 'text.secondary',
          }}
          onClick={handleBack}
        >
          <SvgIcon icon={ArrowLeftIcon} color="inherit" />
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'text.secondary' }}>
            TRANSACTIONS
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <UserInfoLeft>
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'text.primary' }}>
              Identifiant : {userInfo?.id || userId}
            </Typography>
            <Typography sx={{ fontSize: 40, fontWeight: 700, color: 'text.primary' }}>
              {userInfo?.name || '—'}
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 500, color: 'text.secondary' }}>
              {userInfo?.email || '—'}
            </Typography>
          </UserInfoLeft>

          <AvailableBalanceBox>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Typography sx={{ fontSize: 16, fontWeight: 500 }}>MONTANT DISPONIBLE</Typography>
              <Typography sx={{ fontSize: 24, fontWeight: 700 }}>
                {userDetails?.balance || 0}€
              </Typography>
            </Box>
            <Box
              className="iban-box"
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '50px',
                borderRadius: '14px',
                padding: '20px',
                width: '100%',
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {userInfo?.ibanLast4 ? `IBAN **** ${userInfo.ibanLast4}` : '—'}
              </Typography>
            </Box>
          </AvailableBalanceBox>
        </Box>
      </UserInfoCard>

      <Body>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <GridHeader>
            <Typography sx={{ fontSize: 18, fontWeight: 500, letterSpacing: 0.06 }}>
              HISTORIQUE DES TRANSACTIONS
            </Typography>
          </GridHeader>

          <FilterToolbar>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <SearchInputWrapper>
                <InputField
                  fullWidth
                  placeholder="Rechercher"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    shrink: false,
                    startAdornment: (
                      <InputAdornment position="start">
                        <ThemedSearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-input': {
                      padding: '10px 0px',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input': {
                      padding: '10px 0px',
                    },
                  }}
                />
              </SearchInputWrapper>

              <DateRangePicker
                value={dateFilterRange}
                onChange={setDateFilterRange}
                label="Date"
                placeholder="Toutes"
              />
            </Box>

            <FilterButton
              $open={sortOpen}
              onClick={handleSortOpen}
              aria-haspopup="true"
              aria-expanded={sortOpen ? 'true' : undefined}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}
              >
                <FilterButtonCaption>Trier par</FilterButtonCaption>
                <FilterButtonLabel>
                  {SORT_OPTIONS.date.options.find((o) => o.value === sortConfig.value)?.label ??
                    'Récent → Ancien'}
                </FilterButtonLabel>
              </Box>
              <FilterArrow open={sortOpen} />
            </FilterButton>

            <FilterMenu
              anchorEl={sortAnchorEl}
              open={sortOpen}
              onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') handleSortClose();
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <Box sx={{ padding: '8px 0' }}>
                <SortMenuSectionHeader>DATE</SortMenuSectionHeader>
                {SORT_OPTIONS.date.options.map(({ value, label }) => (
                  <SortMenuItem
                    key={value}
                    $selected={sortConfig.value === value}
                    onClick={() => handleSortSelect(value)}
                    disableRipple
                  >
                    {label}
                  </SortMenuItem>
                ))}
              </Box>
            </FilterMenu>

            {!!statusTransaction && (
              <StatusMenu
                anchorEl={statusAnchorEl}
                open={statusMenuOpen}
                onClose={(_, reason) => {
                  if (reason === 'backdropClick' || reason === 'escapeKeyDown')
                    handleStatusMenuClose();
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {[STATUS_VARIANTS.pending, STATUS_VARIANTS.completed, STATUS_VARIANTS.rejected]
                  .filter((status) => status !== statusTransaction?.status)
                  .map((status) => (
                    <StatusMenuItem
                      key={status}
                      onClick={() => handleStatusSelect(status)}
                      disableRipple
                    >
                      <StatusBadge variant={status}>
                        {getTransactionStatusLabel(status)}
                      </StatusBadge>
                    </StatusMenuItem>
                  ))}
              </StatusMenu>
            )}
          </FilterToolbar>

          <Box sx={{ flex: 1, minHeight: 0 }}>
            <Grid
              loading={loading}
              animateRows
              suppressScrollOnNewData
              getRowId={getRowId}
              rowData={rows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              fullWidthCellRenderer={fullWidthCellRenderer}
              pinnedBottomRowData={pinnedBottomRowData}
              isFullWidthRow={isFullWidthRow}
            />
          </Box>
        </Box>
      </Body>
    </>
  );
};

export default UserTransactionsScreen;
